import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { FiTrash2 } from 'react-icons/fi';
import { useCart } from '../contexts/CartContext';
import './CartDrawer.css';

const SIZE_LABELS = { '8oz': '8 oz', '12oz': '12 oz', '16oz': '1 lb' };

export default function CartDrawer() {
    const {
        isDrawerOpen,
        closeDrawer,
        cartItems,
        cartCount,
        removeFromCart,
        updateQuantity,
    } = useCart();
    const drawerRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (isDrawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isDrawerOpen]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') closeDrawer();
        };
        if (isDrawerOpen) {
            document.addEventListener('keydown', handleEsc);
        }
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isDrawerOpen, closeDrawer]);

    const subtotal = cartItems.reduce(
        (sum, item) => sum + item.unitPrice * item.quantity,
        0
    );

    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState('');

    const handleCheckout = async () => {
        setCheckoutLoading(true);
        setCheckoutError('');
        try {
            const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
            const result = await createCheckoutSession({
                items: cartItems,
                origin: window.location.origin,
            });
            if (result.data.url) {
                window.location.href = result.data.url;
            }
        } catch (err) {
            console.error('Checkout error:', err);
            setCheckoutError('Something went wrong. Please try again.');
            setCheckoutLoading(false);
        }
    };

    return (
        <>
            <div
                className={`cd-overlay ${isDrawerOpen ? 'cd-overlay--visible' : ''}`}
                onClick={closeDrawer}
            />
            <div
                ref={drawerRef}
                className={`cd-drawer ${isDrawerOpen ? 'cd-drawer--open' : ''}`}
            >
                <div className="cd-header">
                    <h2 className="cd-title">Your Cart</h2>
                    <button className="cd-close" onClick={closeDrawer}>
                        &times;
                    </button>
                </div>

                <div className="cd-body">
                    {cartItems.length === 0 ? (
                        <div className="cd-empty-wrap">
                            <p className="cd-empty">Your cart is empty.</p>
                            <button
                                className="cd-return-shop"
                                onClick={() => { closeDrawer(); navigate('/our-coffee'); }}
                            >
                                RETURN TO SHOP
                            </button>
                        </div>
                    ) : (
                        cartItems.map((item, index) => (
                            <div key={index} className="cd-item">
                                <div className="cd-item-info">
                                    {item.type === 'merch' ? (
                                        <>
                                            <div className="cd-item-name">{item.name}</div>
                                            <div className="cd-item-details">
                                                {item.color}{item.size && ` · ${item.size}`}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="cd-item-name">
                                                Craft Roast Coffee — {SIZE_LABELS[item.size] || item.size}
                                            </div>
                                            <div className="cd-item-details">
                                                {item.roastLevel &&
                                                    item.roastLevel.charAt(0).toUpperCase() +
                                                        item.roastLevel.slice(1)}{' '}
                                                · {item.originType === 'single' ? 'Single Origin' : 'Blend'}
                                                {item.region && ` · ${item.region}`}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="cd-item-row">
                                    <div className="cd-qty">
                                        <button
                                            onClick={() =>
                                                updateQuantity(index, item.quantity - 1)
                                            }
                                        >
                                            &minus;
                                        </button>
                                        <span>{item.quantity}</span>
                                        <button
                                            onClick={() =>
                                                updateQuantity(index, item.quantity + 1)
                                            }
                                        >
                                            +
                                        </button>
                                    </div>
                                    <span className="cd-item-price">
                                        ${(item.unitPrice * item.quantity).toFixed(2)}
                                    </span>
                                    <button
                                        className="cd-remove"
                                        onClick={() => removeFromCart(index)}
                                        aria-label="Remove item"
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {cartItems.length > 0 && (
                    <div className="cd-footer">
                        <div className="cd-total">
                            <span>
                                Subtotal ({cartCount}{' '}
                                {cartCount === 1 ? 'Item' : 'Items'})
                            </span>
                            <span className="cd-total-price">
                                ${subtotal.toFixed(2)}
                            </span>
                        </div>
                        <p className="cd-tax-note">
                            Taxes, shipping and pickup options available at checkout.
                        </p>
                        {checkoutError && <p className="cd-error">{checkoutError}</p>}
                        <button className="cd-checkout" onClick={handleCheckout} disabled={checkoutLoading}>
                            {checkoutLoading ? 'PROCESSING...' : 'CONTINUE TO CHECKOUT'}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
